<template>
    <div class="md-layout centered-layout">
        <div class="md-layout-item md-size-30 md-medium-size-100 md-xsmall-size-100">
            <md-card>
                <md-card-header class="md-card-header-icon md-card-header-primary">
                    <div class="card-icon">
                        <md-icon>lock</md-icon>
                    </div>
                    <h4 class="title">
                        Two Factor Authentication
                    </h4>
                </md-card-header>

                <md-card-content v-if="!isEnabled">

                    <p>Two Factor Authentication (2FA) is a security feature that requires you to enter a code from an authenticator app in addition to your password when you log in to your account.</p>
                    <p>If you haven't got an authenticator app, you can download one from the <a href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2">Google Play Store</a> or <a href="https://apps.apple.com/us/app/google-authenticator/id388497605">Apple App Store</a>.</p>
                    <p>Once you have the app, scan the QR code below with your authenticator app to get your authentication code.</p>
                    <img class="qr-code" v-if="bootstrap.qrcode" :src="'data:image/png;base64,' + bootstrap.qrcode"
                                 style="max-width:250px;display:block"/>
                    <p>Enter the resulting 6-digit verification code from the app to enable 2FA.</p>
                    <md-field>
                        <label>Code</label>
                        <md-input v-model="code" maxlength="6" autocomplete="one-time-code" />
                    </md-field>

                    <md-button class="md-raised md-primary" @click="enable">Enable 2FA</md-button>
                </md-card-content>

                <md-card-content v-else>
                    <p>Two Factor Authentication (2FA) is currently enabled for your account.</p>
                    <p>If you want to disable 2FA, enter the verification code from your authenticator app.</p>
                    <md-field>
                        <label>Code</label>
                        <md-input v-model="code" maxlength="6" autocomplete="one-time-code" />
                    </md-field>
                    <md-button class="md-raised md-primary" @click="disable">Disable 2FA</md-button>
                </md-card-content>

                
            </md-card>
        </div>

        <md-snackbar :md-active.sync="snackbar.active" :md-type="snackbar.type">
           {{ snackbar.text }}
        </md-snackbar>
    </div>
</template>

<script>
import {mapGetters} from 'vuex';
import {Secret, TOTP} from 'otpauth';
import QRCode from 'qrcode';

const unwrap = res => res?.data ?? res?.response?.data ?? res;

export default {
    name: 'Security',

    data() {
        return {
            loading: true,
            isEnabled: false,
            secretObj: null,
            bootstrap: {qrcode: '', secret: ''},
            code: '',
            snackbar: {active: false, text: '', type: ''},
        };
    },

    computed: {
        ...mapGetters('app', {self: 'getAuthenticatedInitData'}),
    },

    async created() {
        const data = unwrap(await this.$api.get2FAStatus());
        this.isEnabled = !!data.is_enabled;
        if (!this.isEnabled) await this.generateBootstrap();
        this.loading = false;
    },

    methods: {

        async generateBootstrap() {
            try {
                this.secretObj = new Secret();
                const secret = this.secretObj.base32;

                const otpauth = new TOTP({
                    issuer: 'Navitas',
                    label: this.self.user.email,
                    secret: this.secretObj,
                }).toString();

                const dataURL = await QRCode.toDataURL(otpauth);

                this.bootstrap = {
                    secret,
                    qrcode: dataURL.replace(/^data:image\/png;base64,/, ''),
                };
            } catch (e) {
                console.error('[2FA] bootstrap failed', e);
            }
        },

        async enable() {
            if (this.code.length !== 6)
                return this.snack('Enter 6 digits', 'warning');

            try {
                const data = unwrap(
                    await this.$api.enableDisable2FA({
                        data: {
                            google_2fa_secret: this.bootstrap.secret,
                            code: this.code
                        }
                    })
                );

                if (data.status === 'success') {
                    this.isEnabled = !!data.is_enabled;
                    this.code = '';
                    this.snack('Two-Step Login enabled', 'success');
                } else {
                    this.snack(data.error_message || 'Server error', 'danger');
                }
            } catch (err) {
                const data = unwrap(err);
                this.snack(data.error_message || 'Server error', 'danger');
            }
        },

        async disable() {
            if (this.code.length !== 6)
                return this.snack('Enter 6 digits', 'warning');

            try {
                const data = unwrap(
                    await this.$api.enableDisable2FA({
                        data: {
                            google_2fa_secret: '',
                            code: this.code
                        }
                    })
                );

                if (data.status === 'success') {
                    this.isEnabled = !!data.is_enabled;
                    this.code = '';
                    this.snack('Two-Step Login disabled', 'info');
                    if (!this.isEnabled) await this.generateBootstrap();
                } else {
                    this.snack(data.error_message || 'Server error', 'danger');
                }
            } catch (err) {
                const data = unwrap(err);
                this.snack(data.error_message || 'Server error', 'danger');
            }
        },

        snack(text, type = 'info') {
            this.snackbar = {active: true, text, type};
        },
    },
};
</script>

<style scoped>
.qr-code {
    max-width: 200px;
    display: block;
    margin: 0 auto;
}

.centered-layout {
    justify-content: center;
}

</style>
